import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import './RateInfoTooltip.css';

const SIZES = [
  { key: 'xs', label: 'XS' },
  { key: 's', label: 'S' },
  { key: 'm', label: 'M' },
  { key: 'l', label: 'L' },
  { key: 'xl', label: 'XL' }
];

const GAP = 8;
const EDGE = 8;
const ARROW_INSET = 14;

// Where the popover goes for a given cell. Pure so the placement rules are testable
// without a laid-out DOM: returns { hidden: true } when the cell has nothing left to
// point at, otherwise viewport coordinates plus the arrow's offset within the popover.
export const computePlacement = ({ anchor, popover, viewport, bounds }) => {
  const detached = anchor.width === 0 && anchor.height === 0;
  const offViewport = anchor.bottom < EDGE || anchor.top > viewport.height - EDGE;
  const offBoundary =
    !!bounds &&
    (anchor.right < bounds.left ||
      anchor.left > bounds.right ||
      anchor.bottom < bounds.top ||
      anchor.top > bounds.bottom);
  if (detached || offViewport || offBoundary) return { hidden: true };

  const above = anchor.bottom + GAP + popover.height > viewport.height && anchor.top - GAP - popover.height > 0;
  const top = above ? anchor.top - GAP - popover.height : anchor.bottom + GAP;

  const center = anchor.left + anchor.width / 2;
  const maxLeft = Math.max(EDGE, viewport.width - popover.width - EDGE);
  const left = Math.min(Math.max(center - popover.width / 2, EDGE), maxLeft);
  // Clamped against a viewport edge the popover is no longer centred, so the arrow slides.
  const arrowLeft = Math.min(Math.max(center - left, ARROW_INSET), Math.max(popover.width - ARROW_INSET, ARROW_INSET));

  return { hidden: false, above, top: Math.round(top), left: Math.round(left), arrowLeft: Math.round(arrowLeft) };
};

const RateInfoTooltip = ({ data, onClose, anchorEl, boundaryEl }) => {
  const ref = useRef(null);

  // position: fixed lets the popover escape the table's scroll container, so its
  // placement is ours to maintain. Written to node.style in a rAF rather than via
  // setState — a state round-trip lands a frame late and visibly drifts on scroll.
  const place = useCallback(() => {
    const node = ref.current;
    if (!node || !anchorEl) return;

    const placement = computePlacement({
      anchor: anchorEl.getBoundingClientRect(),
      popover: { width: node.offsetWidth, height: node.offsetHeight },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      bounds: boundaryEl?.getBoundingClientRect()
    });

    if (placement.hidden) {
      node.style.visibility = 'hidden';
      return;
    }
    node.style.visibility = 'visible';
    node.style.transform = `translate3d(${placement.left}px, ${placement.top}px, 0)`;
    node.classList.toggle('rit-popover--above', placement.above);
    node.style.setProperty('--rit-arrow-left', `${placement.arrowLeft}px`);
  }, [anchorEl, boundaryEl]);

  // Layout effect so the popover is placed before first paint, not after a flash at 0,0.
  useLayoutEffect(() => {
    if (!anchorEl) return undefined;

    let frame = null;
    const schedule = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(() => {
          frame = null;
          place();
        });
      }
    };

    place();
    // Capture phase: the table's own horizontal scroll does not bubble to window.
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    // Row height can change (inline multiplier edit, revert button) with no scroll or resize.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    if (observer) {
      observer.observe(anchorEl);
      if (ref.current) observer.observe(ref.current);
    }

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (observer) observer.disconnect();
    };
  }, [anchorEl, place]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.rit-anchor')) {
        onClose();
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!data) return null;

  const { veteranRatio, packageDistribution } = data;

  return (
    <div className="rit-popover" ref={ref} role="tooltip">
      <div className="rit-section">
        <div className="rit-heading">Veteran Ratio</div>
        {veteranRatio ? (
          <>
            <div className="rit-ratio-bar-wrap">
              <div className="rit-ratio-bar">
                <div className="rit-ratio-fill" style={{ width: `${Math.round(veteranRatio.ratio * 100)}%` }} />
              </div>
              <span className="rit-ratio-label">{Math.round(veteranRatio.ratio * 100)}%</span>
            </div>
            <div className="rit-ratio-detail">
              {veteranRatio.veteranCount}/{veteranRatio.totalCount} associates (≥90 days)
            </div>
          </>
        ) : (
          <span className="rit-no-data">Not available</span>
        )}
      </div>

      <div className="rit-section">
        <div className="rit-heading">Package Size Mix</div>
        {packageDistribution ? (
          SIZES.map(({ key, label }) => {
            const pct = packageDistribution[key] || 0;
            return (
              <div key={key} className="rit-size-row">
                <span className="rit-size-label">{label}</span>
                <div className="rit-size-bar">
                  <div className={`rit-size-fill rit-size-fill--${key}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="rit-size-pct">{pct}%</span>
              </div>
            );
          })
        ) : (
          <span className="rit-no-data">Not available</span>
        )}
      </div>
    </div>
  );
};

export default RateInfoTooltip;
